import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from 'react-bootstrap';
import {
  faMobile,
  faLaptop,
  faDesktop,
  faNetworkWired,
  faServer,
  faBuilding,
  faHdd,
  faInfoCircle,
  faUsers,
  faShieldAlt,
  faSimCard,
  faSatelliteDish,
  faWifi,
  faTowerCell,
  faLock,
  faCloudUploadAlt,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import './NetworkDiagram.scss';

/**
 * NetworkDiagram - Interactive visualization of the MET Connect® Remote Access solution
 * Displays the connection between user devices and plant networks through the Virtual Network Instance
 * 
 * @returns {JSX.Element} Network diagram component
 */
const NetworkDiagram = () => {
  // State for modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  
  // Combined modal content with all information
  const accessFeatures = [
    {
      title: "Multiple User Accounts",
      description: "Simple firewall rules can allow some users restricted access to only certain devices whilst Engineer accounts can have complete unrestricted network access.",
      icon: faUsers
    },
    {
      title: "Supported on Multiple Platforms",
      description: "Built-in support with every common OS – No client software to install.",
      icon: faLaptop
    },
    {
      title: "Every Major VPN Protocol Supported",
      description: "L2TP/IPSec, OpenVPN, Microsoft SSTP, L2TPv3/IPSec, EtherIP/IPSec",
      icon: faShieldAlt
    },
    {
      title: "No Special SIM Card Provisioning",
      description: "Any off-the-shelf data plan from any mobile provider will work. No requirement for custom APNs, Public IP or Static IP Addresses.",
      icon: faSimCard
    }
  ];

  // Plant connection information
  const plantConnectionFeatures = [
    {
      title: "4G Connectivity",
      description: "Any off-the-shelf data plan from any mobile provider will work. No requirement for custom APNs, Public IP or Static IP Addresses.",
      icon: faTowerCell
    },
    {
      title: "Broadband Connectivity",
      description: "Connect through standard broadband connections with no need for fixed IP addresses or special DNS configurations.",
      icon: faWifi
    },
    {
      title: "Starlink Connectivity",
      description: "Remote sites can use Starlink satellite internet for reliable connectivity even in the most isolated locations.",
      icon: faSatelliteDish
    },
    {
      title: "Layer 2 Ethernet Connection",
      description: "Your remote computer behaves as if it's plugged in directly to your network switch on-site. No requirement for default gateways programmed in all remote network devices.",
      icon: faNetworkWired
    }
  ];

  // Virtual Network Instance information
  const networkFeatures = [
    {
      title: "Virtual Ethernet Switch",
      description: "The MET Connect® Virtual Network Instance functions as a layer 2 Ethernet switch, allowing your remote devices to behave as if they're plugged directly into your on-site network.",
      icon: faNetworkWired
    },
    {
      title: "Enterprise-Grade Security",
      description: "Our platform provides secure communications with end-to-end encryption and multi-factor authentication to ensure only authorized users access your network.",
      icon: faLock
    },
    {
      title: "Cloud Hosting",
      description: "Hosted in secure data centers with redundant infrastructure to ensure high availability and reliability for your critical connections.",
      icon: faCloudUploadAlt
    },
    {
      title: "Network Isolation",
      description: "Each customer's Virtual Network Instance is completely isolated from others, ensuring the highest level of security and privacy for your industrial control systems.",
      icon: faExchangeAlt
    }
  ];

  return (
    <div className="network-diagram">
      {/* Column 1: User Devices */}
      <div className="diagram-column user-devices-column">
        <div 
          className="connection-box interactive-box"
          onClick={() => setShowUserModal(true)}
        >
          <div className="box-content">
            <FontAwesomeIcon icon={faMobile} className="device-icon" />
            <div className="box-text">Mobile User</div>
          </div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
        <div 
          className="connection-box interactive-box"
          onClick={() => setShowUserModal(true)}
        >
          <div className="box-content">
            <FontAwesomeIcon icon={faLaptop} className="device-icon" />
            <div className="box-text">Laptop User</div>
          </div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
        <div 
          className="connection-box interactive-box"
          onClick={() => setShowUserModal(true)}
        >
          <div className="box-content">
            <FontAwesomeIcon icon={faDesktop} className="device-icon" />
            <div className="box-text">Team Users</div>
          </div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
      </div>

      {/* Connection lines */}
      <div className="connection-lines">
        <div className="line top-line"></div>
        <div className="line middle-line"></div>
        <div className="line bottom-line"></div>
      </div>

      {/* Column 2: MET Connect Virtual Network */}
      <div className="diagram-column met-connect-column">
        <div 
          className="met-connect-box interactive-box"
          onClick={() => setShowNetworkModal(true)}
        >
          <div className="met-connect-logo">MET Connect<sup>®</sup></div>
          <div className="network-title">Virtual Network Instance</div>
          <div className="network-icon-container">
            <FontAwesomeIcon icon={faNetworkWired} className="network-icon" />
            <FontAwesomeIcon icon={faServer} className="network-icon" />
          </div>
          <div className="network-subtitle">Layer 2, Virtual Ethernet Switch</div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
      </div>

      {/* Connection lines - removed middle line */}
      <div className="connection-lines">
        <div className="line top-line"></div>
        <div className="line bottom-line"></div>
      </div>

      {/* Column 3: Plant Networks - Only Plant 1 and Plant 2, with Plant 1 being taller */}
      <div className="diagram-column plant-column">
        <div 
          className="connection-box plant-box plant-1 interactive-box"
          onClick={() => setShowPlantModal(true)}
        >
          <div className="box-content plant-content">
            <div className="plant-title">Plant 1</div>
            <div className="plant-device-section">
              <FontAwesomeIcon icon={faHdd} className="plant-icon" />
              <div className="plant-device-label">Modem/VPN Client</div>
            </div>
            <div className="plant-connection-line"></div>
            <div className="plant-device-section">
              <FontAwesomeIcon icon={faBuilding} className="plant-icon" />
              <div className="plant-device-label">PLC's, HMI's, SCADA Server</div>
            </div>
          </div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
        <div 
          className="connection-box plant-box plant-2 interactive-box"
          onClick={() => setShowPlantModal(true)}
        >
          <div className="box-content plant-content">
            <div className="plant-title">Plant 2</div>
            <div className="plant-device-section">
              <FontAwesomeIcon icon={faHdd} className="plant-icon" />
              <div className="plant-device-label">Modem/VPN Client</div>
            </div>
          </div>
          <div className="hover-message">
            <FontAwesomeIcon icon={faInfoCircle} /> Click for more information
          </div>
        </div>
      </div>

      {/* Comprehensive Modal for displaying all user access information */}
      <Modal 
        show={showUserModal} 
        onHide={() => setShowUserModal(false)}
        centered
        size="lg"
        className="network-diagram-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faNetworkWired} className="modal-icon me-2" />
            MET Connect<sup>®</sup> Remote Access Features
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-features">
            {accessFeatures.map((feature, index) => (
              <div key={index} className="modal-feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                </div>
                <div className="feature-content">
                  <h5>{feature.title}</h5>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal for displaying plant connection information */}
      <Modal 
        show={showPlantModal} 
        onHide={() => setShowPlantModal(false)}
        centered
        size="lg"
        className="network-diagram-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBuilding} className="modal-icon me-2" />
            Plant Connection Options
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-features">
            {plantConnectionFeatures.map((feature, index) => (
              <div key={index} className="modal-feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                </div>
                <div className="feature-content">
                  <h5>{feature.title}</h5>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal for displaying virtual network information */}
      <Modal 
        show={showNetworkModal} 
        onHide={() => setShowNetworkModal(false)}
        centered
        size="lg"
        className="network-diagram-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faServer} className="modal-icon me-2" />
            MET Connect<sup>®</sup> Virtual Network Instance
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-intro">
            <p>The MET Connect® Virtual Network Instance is the secure core of our remote access solution. It creates a virtual bridge between your users and your plant networks, providing enterprise-grade security with simple connectivity.</p>
          </div>
          <div className="modal-features">
            {networkFeatures.map((feature, index) => (
              <div key={index} className="modal-feature-item">
                <div className="feature-icon-wrapper">
                  <FontAwesomeIcon icon={feature.icon} className="feature-icon" />
                </div>
                <div className="feature-content">
                  <h5>{feature.title}</h5>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NetworkDiagram;